/**
 * Personelin avans talep formu.
 */

import { useEffect, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { TrendingUp, AlertCircle } from 'lucide-react'
import { avansTalepEt } from '@/lib/services/avans-service'
import { maxAvansTutari } from '@/lib/services/maas-yardimci'
import type { KullaniciPersonel } from '@/types'

interface Props {
  acik: boolean
  personel: KullaniciPersonel
  onKapat: () => void
  onKaydedildi: () => void
}

// 🚧 TEST MODU: tarih kontrolünü kapat (avans-service ile uyumlu)
const TEST_TARIH_KONTROLU_KAPALI = false

function buAyStr(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export function AvansTalepModal({ acik, personel, onKapat, onKaydedildi }: Props) {
  const [tutar, setTutar] = useState<string>('')
  const [aciklama, setAciklama] = useState<string>('')
  const [maxTutar, setMaxTutar] = useState<number>(0)
  const [yukleniyor, setYukleniyor] = useState(false)
  const [kaydediliyor, setKaydediliyor] = useState(false)
  const [hata, setHata] = useState<string | null>(null)

  useEffect(() => {
    if (!acik) return
    setTutar('')
    setAciklama('')
    setHata(null)
    setYukleniyor(true)
    maxAvansTutari(personel, buAyStr())
      .then(setMaxTutar)
      .catch((err) => {
        console.error(err)
        setHata('Maksimum tutar hesaplanamadı.')
      })
      .finally(() => setYukleniyor(false))
  }, [acik, personel])

  const bugun = new Date().getDate()
  const tarihUygun = TEST_TARIH_KONTROLU_KAPALI ? true : bugun >= 15

  async function kaydet(ev: React.FormEvent) {
    ev.preventDefault()
    const sayi = parseFloat(tutar)
    if (isNaN(sayi) || sayi <= 0) {
      setHata('Geçerli bir tutar gir.')
      return
    }
    if (sayi > maxTutar) {
      setHata(`Maksimum talep tutarı ${maxTutar} ₺.`)
      return
    }

    setKaydediliyor(true)
    setHata(null)
    try {
      await avansTalepEt({
        personelId: personel.id,
        tutar: sayi,
        aciklama,
      })
      onKaydedildi()
    } catch (err) {
      setHata((err as Error).message)
    } finally {
      setKaydediliyor(false)
    }
  }

  return (
    <Modal acik={acik} onKapat={onKapat} baslik="Avans Talebi" boyut="md">
      {!tarihUygun ? (
        <div className="text-center py-6">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <p className="font-semibold text-gray-900 mb-2">
            Avans talebi henüz açık değil
          </p>
          <p className="text-sm text-gray-600">
            Avans talepleri sadece <strong>ayın 15'inden sonra</strong> yapılabilir.
            <br />
            Bugün ayın {bugun}. günü.
          </p>
          <button onClick={onKapat} className="btn-secondary mt-4 w-full">
            Tamam
          </button>
        </div>
      ) : yukleniyor ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
          <p className="text-sm text-gray-600 mt-2">Hesaplanıyor...</p>
        </div>
      ) : maxTutar === 0 ? (
        <div className="text-center py-6">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <p className="font-semibold text-gray-900 mb-2">Avans hakkın yok</p>
          <p className="text-sm text-gray-600">
            Bu ay zaten yarı maaşına yakın avans almış olabilirsin. BOSS ile konuş.
          </p>
          <button onClick={onKapat} className="btn-secondary mt-4 w-full">
            Tamam
          </button>
        </div>
      ) : (
        <form onSubmit={kaydet} className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-blue-700" />
              <p className="text-sm font-semibold text-blue-900">
                Maksimum talep edebileceğin
              </p>
            </div>
            <p className="text-2xl font-bold text-blue-900">
              {maxTutar.toLocaleString('de-DE')} ₺
            </p>
            <p className="text-xs text-blue-700 mt-1">
              Bu ayki tahmini hak edişinin yarısı kadar avans alabilirsin.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Talep Tutarı (₺) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="1"
              max={maxTutar}
              step="any"
              value={tutar}
              onChange={(e) => setTutar(e.target.value)}
              className="input-base text-lg font-semibold"
              placeholder="0"
              autoFocus
            />
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={() => setTutar(Math.floor(maxTutar / 2).toString())}
                className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-medium"
              >
                Yarısı
              </button>
              <button
                type="button"
                onClick={() => setTutar(maxTutar.toString())}
                className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-medium"
              >
                Tamamı
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Açıklama (opsiyonel)
            </label>
            <textarea
              rows={2}
              value={aciklama}
              onChange={(e) => setAciklama(e.target.value)}
              className="input-base"
              placeholder="Örn: Kira ödemesi, sağlık masrafı..."
            />
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
            ⚠️ BOSS senin talebine 3 cevap verebilir:
            <ul className="list-disc pl-5 mt-1 space-y-0.5">
              <li>Tam tutarı onaylar</li>
              <li>Daha düşük bir tutar teklif eder (sen kabul/red edersin)</li>
              <li>Reddeder</li>
            </ul>
          </div>

          {hata && (
            <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-xl p-3">
              {hata}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onKapat}
              className="btn-secondary flex-1"
              disabled={kaydediliyor}
            >
              İptal
            </button>
            <button type="submit" className="btn-primary flex-1" disabled={kaydediliyor}>
              {kaydediliyor ? 'Gönderiliyor...' : 'Talep Gönder'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}
